import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'
import { useFormik } from "formik"
import * as Yup from "yup"

// components
import { CButton } from '@coreui/react-pro'
import AppCard from 'src/components/AppCard'
import AppCardHeader from 'src/components/card/AppCardHeader'
import AppDetails from '../../components/details/time-off-request-details/AppDetails'
import AppModalConfirmation from 'src/components/modal/AppModalConfirmation'
import AppModalFilePreview from 'src/components/modal/AppModalFilePreview'

// helpers
import Axios from 'src/helpers/axios'
import useToastMessage from 'src/helpers/toast-message'

// styling
import { styleButtonVars2 } from '../../components/custom-styles/buttonStyles'

const TimeOffRequestDetails = () => {
  const navigate = useNavigate()
  const params = useParams()
  const { id } = params
  const { showError, showSuccess } = useToastMessage();

  const [isLoading, setIsLoading] = useState(false)
  const [timeOffRequestDetails, setTimeOffRequestDetails] = useState('')
  const [showPendingApproval, setShowPendingApproval] = useState({ show: false, item: {} })
  const [showPendingCancellation, setShowPendingCancellation] = useState({ show: false, item: {} })

  const [openFilePreview, setOpenFilePreview] = useState(false)

  const formik = useFormik({
    initialValues: {
      code: timeOffRequestDetails?.leave_type?.code || '',
      type: timeOffRequestDetails?.leave_type?.name || '',
      duration: timeOffRequestDetails?.days_count || '',
      notes: timeOffRequestDetails?.notes || '',
      is_emergency_over_4_hours: timeOffRequestDetails?.is_emergency_over_4_hours || false,
      approval_request: '',
      approval_notes: '',
      approval_days: 1,
      unpaid_category: timeOffRequestDetails?.employee?.leave_balances?.length > 0 ? Number(timeOffRequestDetails?.employee?.leave_balances[0]?.accrued_balance) - Number(timeOffRequestDetails?.employee?.leave_balances[0]?.used_balance) > 0 ? "LWP" : "LOP" : '',
      is_available_balance: timeOffRequestDetails?.employee?.leave_balances?.length > 0 ? Number(timeOffRequestDetails?.employee?.leave_balances[0]?.accrued_balance) - Number(timeOffRequestDetails?.employee?.leave_balances[0]?.used_balance) > 0 : false,
      decline_reason: '',
      approval_cancellation_request: '',
      cancellation_reason: timeOffRequestDetails?.cancellation_request_reason || '',
      cancellation_rejection_reason: '',
      available_balance: timeOffRequestDetails?.employee?.leave_balances?.length > 0 ? Number(timeOffRequestDetails?.employee?.leave_balances[0]?.accrued_balance) - Number(timeOffRequestDetails?.employee?.leave_balances[0]?.used_balance) : 0,
    },
    validationSchema: Yup.object().shape({
      approval_request: Yup.string()
        .test(
          'required-if-pending',
          'Please select approval decision.',
          (value) => {
            if (timeOffRequestDetails?.status === 'pending') {
              return !!value
            }
            return true
          }
        ),
      approval_days: Yup.number()
        .when(['approval_request', 'code'], {
          is: (approval_request, code) =>
            approval_request === 'approved' && (code === 'SICK' || code === 'BEREAVEMENT'),
          then: (schema) => schema.required("Please enter approval days.").positive("Approval days must be a positive number"),
          otherwise: (schema) => schema.notRequired(),
        }),
      unpaid_category: Yup.string()
        .when(['approval_request', 'code'], {
          is: (approval_request, code) =>
            approval_request === 'approved' && code === 'UNPAID',
          then: (schema) => schema.required("Please select unpaid category."),
          otherwise: (schema) => schema.notRequired(),
        }),
      decline_reason: Yup.string()
        .when('approval_request', {
          is: 'decline',
          then: (schema) => schema.required("Please enter decline reason."),
          otherwise: (schema) => schema.notRequired(),
        }),
      approval_cancellation_request: Yup.string()
        .test(
          'required-if-pending-cancellation',
          'Please select approval decision for cancellation request.',
          (value) => {
            if (timeOffRequestDetails?.status === 'pending_cancellation') {
              return !!value
            }
            return true
          }
        ),
      cancellation_rejection_reason: Yup.string()
        .when('approval_cancellation_request', {
          is: 'approved',
          then: (schema) => schema.required("Please enter cancellation rejection reason."),
          otherwise: (schema) => schema.notRequired(),
        }),
    }),
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting }) => {
      handleSave(values)
      setSubmitting(false)
    },
  });

  const [approvalRequestValue, setApprovalRequestValue] = useState('')
  const [approvalCancellationRequestValue, setApprovalCancellationRequestValue] = useState('')

  const handleSelectApprovalRequest = (selectedOption) => {
    setApprovalRequestValue(selectedOption?.value || '')
    formik.setFieldValue('approval_request', selectedOption?.value || '')
    formik.setFieldValue('approval_notes', '')
    formik.setFieldValue('decline_reason', '')
  }

  const handleSelectApprovalCancellationRequest = (selectedOption) => {
    setApprovalCancellationRequestValue(selectedOption?.value || '')
    formik.setFieldValue('approval_cancellation_request', selectedOption?.value || '')
  }

  const handleSelectUnpaidCategory = (selectedOption) => {
    formik.setFieldValue('unpaid_category', selectedOption?.value || '')
  }

  useEffect(() => {
    if (formik.values.available_balance < formik.values.duration) {
      formik.setFieldValue('approval_days', Number(formik.values.duration) - Number(formik.values.available_balance))
    }
  }, [formik.values.available_balance, formik.values.approval_request])

  useEffect(() => {
    fetchTimeOffRequestDetails()
  }, [id]);

  const fetchTimeOffRequestDetails = () => {
    Axios({
      method: "get",
      url: `/api/tracker/v1/leave/request/${id}`,
    })
      .then(({ data }) => {
        if (data?.success) {
          setTimeOffRequestDetails(data?.value)
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      });
  }

  const handleCardHeaderButton = () => {
    if (timeOffRequestDetails?.status === 'pending') {
      setShowPendingApproval({ show: true, item: {} })
    } else if (timeOffRequestDetails?.status === 'pending_cancellation') {
      setShowPendingCancellation({ show: true, item: {} })
    }
  }

  const handleInputValue = (values, status) => {
    if (status === 'pending') {
      if (values.approval_request === 'approved') {
        const payload = {
          approval_request: values.approval_request,
          approve_notes: values.approval_notes ?? null,
        }
        if (values?.code == 'SICK' || values?.code == 'BEREAVEMENT') {
          payload.approval_days = values.approval_days
        }
        if (values?.code == 'EMERGENCY') {
          payload.approval_days = timeOffRequestDetails?.is_emergency_over_4_hours ? timeOffRequestDetails?.days_count : 1
        }
        if (values?.code == 'UNPAID') {
          payload.unpaid_category = values.unpaid_category
        }
        return payload
      } else if (values.approval_request === 'decline') {
        return {
          approval_request: values.approval_request,
          decline_reason: values.decline_reason,
        }
      }
    } else if (status === 'pending_cancellation') {
      const payload = {
        approval_cancellation_request: values.approval_cancellation_request,
      }
      if (values.approval_cancellation_request === 'approved') {
        payload.rejection_reason = values.cancellation_rejection_reason
      }
      return payload
    }

    return {}
  }

  const handleSave = (values) => {
    const inputPayload = handleInputValue(values, timeOffRequestDetails?.status)
    handlePost(inputPayload)
  }

  const handlePost = (payload) => {
    setIsLoading(true)

    let url = `/api/tracker/v1/leave/request/${id}`

    if (timeOffRequestDetails?.status === 'pending') {
      if (payload.approval_request === 'approved') {
        url = `/api/tracker/v1/leave/request/${id}/approve`
      } else if (payload.approval_request === 'decline') {
        url = `/api/tracker/v1/leave/request/${id}/decline`
      }

      Axios({
        method: "post",
        url,
        data: payload
      })
        .then(({ data }) => {
          if (data?.success) {
            showSuccess('Time-off request has been successfully updated')
            setShowPendingApproval({ show: false, item: {} })
            fetchTimeOffRequestDetails()
          }
        })
        .catch((err) => {
          console.error(err);
          const errMsg = err?.response?.data?.message
          showError(errMsg)
        })
        .finally(() => {
          setIsLoading(false)
        })
    } else if (timeOffRequestDetails?.status === 'pending_cancellation') {
      if (payload.approval_cancellation_request === 'approved') {
        url = `/api/tracker/v1/leave/request/${id}/reject-cancel`
      } else if (payload.approval_cancellation_request === 'cancelled') {
        url = `/api/tracker/v1/leave/request/${id}/approve-cancel`
      }

      Axios({
        method: "post",
        url,
        data: payload
      })
        .then(({ data }) => {
          if (data?.success) {
            showSuccess('Time-off cancellation request has been successfully updated')
            setShowPendingCancellation({ show: false, item: {} })
            fetchTimeOffRequestDetails()
          }
        })
        .catch((err) => {
          console.error(err);
          const errMsg = err?.response?.data?.message
          showError(errMsg)
        })
        .finally(() => {
          setIsLoading(false)
        })
    } else {
      setIsLoading(false)
    }
  }

  return (
    <>
      {openFilePreview && (
        <AppModalFilePreview
          setOpenFilePreview={setOpenFilePreview}
          file={openFilePreview}
        />
      )}
      {
        showPendingApproval.show && (
          <AppModalConfirmation
            confirmationType='pending-approval-time-off'
            showModal={showPendingApproval}
            setShowModal={setShowPendingApproval}
            values={formik.values}
            errors={formik.errors}
            handleChange={formik.handleChange}
            approvalRequestValue={approvalRequestValue}
            handleSelectApprovalRequest={handleSelectApprovalRequest}
            handleSelectUnpaidCategory={handleSelectUnpaidCategory}
            handleSubmit={formik.handleSubmit}
            isLoading={isLoading}
          />
        )
      }
      {
        showPendingCancellation.show && (
          <AppModalConfirmation
            confirmationType='pending-cancellation-time-off'
            showModal={showPendingCancellation}
            setShowModal={setShowPendingCancellation}
            values={formik.values}
            errors={formik.errors}
            handleChange={formik.handleChange}
            approvalCancellationRequestValue={approvalCancellationRequestValue}
            handleSelectApprovalCancellationRequest={handleSelectApprovalCancellationRequest}
            handleSubmit={formik.handleSubmit}
            isLoading={isLoading}
          />
        )
      }
      <div className='d-grid gap-2 d-md-flex justify-content-md-start mb-3'>
        <CButton style={styleButtonVars2} onClick={() => navigate('/time-off-request/list')}>
          Back
        </CButton>
      </div>
      <AppCard
        className='mt-4 mb-4'
        headerTitle={
          <AppCardHeader
            headerTitle={<h4>Time-Off Request Details</h4>}
            {
              ...(timeOffRequestDetails?.status === 'pending' || timeOffRequestDetails?.status === 'pending_cancellation') && {
                headerButton: "Set Approval",
                handleCardHeaderButton: handleCardHeaderButton
              }
            }
          />
        }
        bodyContent={
          <AppDetails
            timeOffRequestDetails={timeOffRequestDetails}
            handleViewFile={(fileUrl) => setOpenFilePreview(fileUrl)}
          />
        }
      />
    </>
  )
}

export default TimeOffRequestDetails